import React, { useEffect, useMemo, useRef } from 'react';
import { ApolloClient, ApolloLink, CombinedGraphQLErrors, InMemoryCache, ServerError } from '@apollo/client/core';
import { ApolloProvider } from '@apollo/client/react';
import { HttpLink } from '@apollo/client/link/http';
import { ErrorLink } from '@apollo/client/link/error';
import { Observable } from '@apollo/client/utilities';
import { DELIVERY_URL } from '../../api';
import { RemoveTypenameFromVariablesLink } from '@apollo/client/link/remove-typename';
import { useMessageService, ErrorMessage } from 'framework/ui/Message';
import useAuthenticationTokenRepository from 'infrastructure/security/authentication/tokenprovider/authenticationTokenRepository';
import { AuthenticationToken } from 'infrastructure/security/authentication/tokenprovider/authenticationToken.types';
import { captureException } from '@sentry/react';

interface GraphQLProviderProps {
  children: React.ReactNode;
}

const GraphQLProvider: React.FC<GraphQLProviderProps> = ({ children }) => {
  const { getToken } = useAuthenticationTokenRepository();
  const { addMessage } = useMessageService();

  const getTokenRef = useRef<() => Promise<AuthenticationToken | undefined>>(getToken);

  useEffect(() => {
    getTokenRef.current = getToken;
  }, [getToken]);

  const apolloClient = useMemo(() => {
    const GRAPHQL_URL = `${DELIVERY_URL}/graphql`;

    const httpLink = new HttpLink({
      uri: GRAPHQL_URL,
      credentials: 'include',
    });

    const authLink = new ApolloLink((operation, forward) => {
      return new Observable(observer => {
        getTokenRef
          .current()
          .then(authToken => {
            const tokenString = authToken?.token;
            const headers = { ...operation.getContext().headers };

            if (tokenString) {
              headers['Authorization'] = `Bearer ${tokenString}`;
            }

            headers['Content-Type'] = 'application/json';
            operation.setContext({ headers });

            forward(operation).subscribe(observer);
          })
          .catch(() => {
            const headers = { ...operation.getContext().headers };
            headers['Content-Type'] = 'application/json';
            operation.setContext({ headers });
            forward(operation).subscribe(observer);
          });
      });
    });

    const errorLink = new ErrorLink(({ error, operation }) => {
      if (CombinedGraphQLErrors.is(error)) {
        captureException(error, {
          tags: {
            source: 'graphql',
            operation: operation.operationName,
          },
        });

        addMessage(new ErrorMessage(`GraphQL error: ${error.message}`));
      } else if (ServerError.is(error)) {
        addMessage(
          new ErrorMessage(`Server error: ${operation.operationName}
          ${error.message}`),
        );
      } else if (error) {
        addMessage(
          new ErrorMessage(`Network error: ${operation.operationName}
          ${error.message}`),
        );
      }
    });

    const removeTypenameLink = new RemoveTypenameFromVariablesLink();

    return new ApolloClient({
      link: ApolloLink.from([removeTypenameLink, errorLink, authLink, httpLink]),
      cache: new InMemoryCache(),
      defaultOptions: {
        watchQuery: {
          errorPolicy: 'all',
          fetchPolicy: 'cache-and-network',
        },
        query: {
          errorPolicy: 'all',
        },
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return <ApolloProvider client={apolloClient}>{children}</ApolloProvider>;
};

export default GraphQLProvider;
